import { Link } from "react-router-dom";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { formatDateTime, formatNumber } from "@/lib/format";

type LlmExtraction = components["schemas"]["BillLlmExtractionResponse"];

// Extraction costs are sub-cent, so plain 2dp currency rounds most of them to $0.00. Show up to 6dp.
const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

// Decimal fields arrive as `string | number` in the generated types (C# decimal), so coerce.
export function formatUsd(value: number | string | null | undefined): string {
  if (value == null) return "n/a";
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? usdFormatter.format(n) : "n/a";
}

function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="text-sm font-medium tabular-nums">{children}</div>
    </div>
  );
}

export function LlmExtractionFields({ record }: { record: LlmExtraction }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
      <Metric label="Model">
        <Badge variant="secondary" className="font-mono text-xs">
          {record.model}
        </Badge>
      </Metric>
      <Metric label="Total cost">
        <span className="font-semibold">{formatUsd(record.totalCostUsd)}</span>
      </Metric>
      <Metric label="Input cost">{formatUsd(record.inputCostUsd)}</Metric>
      <Metric label="Output cost">{formatUsd(record.outputCostUsd)}</Metric>

      <Metric label="Input tokens">{formatNumber(record.inputTokens)}</Metric>
      <Metric label="Output tokens">{formatNumber(record.outputTokens)}</Metric>
      <Metric label="Cache read tokens">{formatNumber(record.cacheReadTokens)}</Metric>
      <Metric label="Cache write tokens">{formatNumber(record.cacheCreationTokens)}</Metric>

      <Metric label="Effort">{record.effort}</Metric>
      <Metric label="Thinking">{record.thinkingEnabled ? "on" : "off"}</Metric>
      <Metric label="Max tokens">{formatNumber(record.maxTokens)}</Metric>
      <Metric label="Duration">{formatNumber(Math.round(Number(record.durationMs ?? 0)))} ms</Metric>

      <Metric label="Extracted at">{formatDateTime(record.extractedAt)}</Metric>

      {/* Only for company-inbox documents: the company ran and paid for the extraction. */}
      {record.companyId && (
        <Metric label="Company">
          <Link to={`/companies/${record.companyId}`}>
            <Badge
              variant="secondary"
              className="font-mono text-xs hover:bg-primary/20"
              title={record.companyId}
            >
              {record.companyId.slice(0, 8)}...
            </Badge>
          </Link>
        </Metric>
      )}
    </div>
  );
}
